/**
 * #2069 — one app-made offer per idea found under a chat reply.
 * Ideas are proposed story_entities from the live pass and the summary sweep.
 * Person, place, thing, and event only. No per-reply cap.
 */

import {
  CAST_PEOPLE,
  CAST_PLACE,
  IDEA_KIND_EVENT,
  IDEA_KIND_THING,
} from './writerCopy';

export const CARD_OFFER_TYPES = new Set(['character', 'place', 'object', 'event']);
export const CARD_OFFER_ORIGINS = new Set(['live', 'summary_sweep']);

export function isCardOffer(entity) {
  if (!entity) return false;
  const type = String(entity.entity_type || '').toLowerCase();
  if (!CARD_OFFER_TYPES.has(type)) return false;
  if (!CARD_OFFER_ORIGINS.has(entity.originating_pass)) return false;
  if (entity.offer_saved) return true;
  return entity.status === 'proposed';
}

/** Character and place have their own Notes categories. World and Plot hold one note each, so a thing or an event stays Unsorted. */
export function noteCategoryForOffer(entityType) {
  const type = String(entityType || '').toLowerCase();
  if (type === 'character') return 'Character';
  if (type === 'place') return 'Setting/Location';
  return 'Unsorted';
}

export function offerKindLabel(entityType) {
  const type = String(entityType || '').toLowerCase();
  if (type === 'character') return CAST_PEOPLE;
  if (type === 'place') return CAST_PLACE;
  if (type === 'object') return IDEA_KIND_THING;
  if (type === 'event') return IDEA_KIND_EVENT;
  return '';
}

function speakerOf(message) {
  return message?.speaker || message?.role || '';
}

/**
 * Attach each offer to the reply that follows the message it was found in.
 * A writer message with no reply after it (Mute) keeps the offer on that message.
 * Offers whose message is not in this thread come back as loose.
 */
export function groupCardOffers(messages, entities) {
  const bySource = new Map();
  const loose = [];
  (entities || []).filter(isCardOffer).forEach((offer) => {
    const sourceId = offer.source_message_id ? String(offer.source_message_id) : '';
    if (!sourceId) {
      loose.push(offer);
      return;
    }
    const list = bySource.get(sourceId) || [];
    list.push(offer);
    bySource.set(sourceId, list);
  });

  const byMessage = new Map();
  const claimed = new Set();
  const thread = messages || [];
  thread.forEach((message, index) => {
    const id = message?.id != null ? String(message.id) : '';
    const found = id ? bySource.get(id) : null;
    if (!found) return;
    claimed.add(id);
    let targetId = id;
    if (speakerOf(message) === 'user') {
      for (let next = index + 1; next < thread.length; next += 1) {
        const later = thread[next];
        if (speakerOf(later) === 'assistant') {
          targetId = String(later.id);
          break;
        }
        if (speakerOf(later) === 'user') break;
      }
    }
    const existing = byMessage.get(targetId) || [];
    byMessage.set(targetId, existing.concat(found));
  });

  bySource.forEach((found, sourceId) => {
    if (!claimed.has(sourceId)) loose.push(...found);
  });
  return { byMessage, loose };
}
